import { Colors, Icon, Text, type CustomRadioOptionProps } from '@amelio/ui-library';

import type { IconProps } from '@amelio/ui-library';

const GLYPH_SIZE = 20;
const MARKER_GLYPH_SIZE = 12;
const MARKER_GLYPH_STROKE = 3;

const TILE_TITLE_TYPE = 'text-[14px] leading-[20px]';
const TILE_DESCRIPTION_TYPE = 'text-[12px] leading-[19.5px]';

export interface AudienceTileProps extends CustomRadioOptionProps {
    description: string;
    iconName: IconProps['name'];
}

export const AudienceTile = ({
    option,
    isChecked,
    isDisabled,
    isReadOnly = false,
    onSelect,
    name,
    description,
    iconName,
    dataTestId,
}: AudienceTileProps) => {
    const labelId = `${name}-${option.value}-label`;
    const descriptionId = `${name}-${option.value}-description`;

    return (
        <label
            className={[
                'flex h-full items-start rounded-[12px] border text-left',
                'gap-[12px] p-[16px] transition-all',
                'has-[:focus-visible]:ring-primary has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-offset-2',
                isReadOnly ? 'cursor-default' : 'cursor-pointer',
                isChecked
                    ? 'border-transparent bg-white shadow-[0_8px_20px_-12px_color-mix(in_srgb,var(--color-step-rail-shadow)_35%,transparent)] ring-2 ring-[var(--color-cycle-tile-ring)]'
                    : `border-[var(--color-cycle-tile-idle-border)] bg-[var(--color-cycle-tile-idle-surface)] ${isReadOnly ? '' : 'hover:bg-white/85'}`,
                isDisabled ? 'cursor-not-allowed opacity-50' : '',
            ].join(' ')}
            data-testid={dataTestId}
        >
            <input
                type={'radio'}
                className={'sr-only'}
                name={name}
                value={option.value}
                checked={isChecked}
                disabled={isDisabled}
                readOnly={isReadOnly}
                aria-labelledby={labelId}
                aria-describedby={descriptionId}
                aria-readonly={isReadOnly || undefined}
                onChange={onSelect}
                onClick={isReadOnly ? (event) => event.preventDefault() : undefined}
            />

            <span
                className={[
                    'inline-flex shrink-0 items-center justify-center rounded-[8px]',
                    'h-[40px] w-[40px]',
                    isChecked
                        ? 'from-step-rail-violet bg-linear-to-br to-[var(--color-cycle-tile-violet-deep)]'
                        : 'bg-[var(--color-cycle-tile-icon-idle-surface)]',
                ].join(' ')}
            >
                <Icon
                    name={iconName}
                    size={GLYPH_SIZE}
                    color={isChecked ? Colors.WHITE : Colors.STEP_RAIL_VIOLET}
                    ariaHidden={true}
                />
            </span>

            <span className={'min-w-0 flex-1'}>
                <span className={'flex items-center justify-between gap-[8px]'}>
                    <Text
                        as={'span'}
                        id={labelId}
                        weight={'semibold'}
                        color={Colors.SLATE}
                        className={TILE_TITLE_TYPE}
                    >
                        {option.label}
                    </Text>

                    <span
                        className={[
                            'inline-flex shrink-0 items-center justify-center rounded-full border',
                            'h-[20px] w-[20px]',
                            isChecked
                                ? 'border-transparent bg-[var(--color-cycle-tile-violet-bright)]'
                                : 'border-[var(--color-cycle-tile-marker-idle)] bg-white',
                        ].join(' ')}
                    >
                        {isChecked && (
                            <Icon
                                name={'essential-checkmark'}
                                size={MARKER_GLYPH_SIZE}
                                strokeWidth={MARKER_GLYPH_STROKE}
                                color={Colors.WHITE}
                                ariaHidden={true}
                            />
                        )}
                    </span>
                </span>

                <Text
                    as={'span'}
                    id={descriptionId}
                    color={Colors.STEP_RAIL_MUTED}
                    className={`${TILE_DESCRIPTION_TYPE} mt-[2px] block`}
                >
                    {description}
                </Text>
            </span>
        </label>
    );
};
